import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ConfigSchema, SchemaField, VehicleRow } from "./configTypes";
import {
  buildChanges,
  Draft,
  DraftValue,
  draftFromVehicle,
  FieldChange,
  fieldByName,
  isEditable,
  toDraft,
  validateAll,
  validateDraft,
  blockedOptions,
  effectiveAttrs,
  ruleCascades,
  ruleErrors,
  READINESS_FIELDS,
  touchesReadiness,
} from "./configModel";
import {
  ConfigConflictError,
  FleetStateApiError,
  patchVehicle,
} from "./configApi";
import { Vehicle, vehicleFromRow } from "./data";

/**
 * One edit session for one tractor's config record.
 *
 * This used to live inside ConfigPanel. It is a hook because the session now
 * has **two** surfaces: the panel itself, and the action bar pinned to the top
 * of the detail page — an operator who has scrolled to the bottom of a long
 * record should not have to scroll back up (or hunt for the footer) to save.
 * Two components rendering from two copies of "what is pending" is precisely
 * how a save button comes to disagree with the form above it, so there is one
 * copy and both read it.
 *
 * The save contract is unchanged and deliberate: a single all-or-nothing PATCH
 * of only the dirty fields, guarded by the `config_version` that was read. One
 * edit, one change-set, one version bump — two operators editing the same
 * tractor get a 409 rather than one silently overwriting the other.
 *
 * ## The draft outlives the component
 *
 * The draft used to be plain component state, and the detail view is unmounted
 * by every exit but one: the browser's Back button, a Navbar link, a Cognito
 * redirect when the token renews, a refresh. Only the sibling rail asked before
 * throwing the draft away. The app is on `BrowserRouter`, which cannot block
 * navigation, so instead of trying to stop the exit the draft is written to
 * `sessionStorage` on every keystroke and restored when the same tractor is
 * opened again — provided the record has not moved on (`config_version` must
 * match; a draft written against a row somebody else has since edited is
 * dropped, and the page says so). A `beforeunload` prompt still covers closing
 * the tab, which is the one exit sessionStorage does not survive.
 */
export interface SavedNote {
  /** Fields changed plus attestations re-confirmed. */
  count: number;
  /** The row's version after the write, when the API reported one. */
  version: number | null;
  /** Epoch ms of the successful save. */
  at: number;
}

export interface ConfigEditor {
  editing: boolean;
  draft: Draft;
  errors: Record<string, string>;
  /** Attestations ticked as re-verified this session, minus any also edited. */
  confirmations: SchemaField[];
  /** Field names whose value actually moved — the dirty set. */
  dirtyNames: ReadonlySet<string>;
  /** The reviewable diff: field, from, to. */
  list: FieldChange[];
  /**
   * Changes a cross-field rule will make because of the operator's own
   * (`ruleCascades`): previewed in Review under "Applied by rule", counted
   * as pending, and never sent — the API applies them.
   */
  cascades: FieldChange[];
  /** Option value -> why a rule refuses it on the row as the draft stands. */
  blockedFor: (field: SchemaField) => Record<string, string>;
  /** Dirty fields + confirmations + rule cascades. What "Review N changes" counts. */
  pendingCount: number;
  hasErrors: boolean;
  /** True while a write is in flight. */
  saving: boolean;
  /** The review dialog is open. */
  reviewing: boolean;
  /** A failed save, already phrased for the reader. */
  saveError: string | null;
  /**
   * Non-null after a 409: the *labels* of the fields that moved underneath
   * the draft. Labels rather than attribute names because this is read by the
   * operator, in a banner, at the moment they most need to understand it.
   */
  conflictFields: string[] | null;
  /**
   * Labels of untouched fields whose value was refreshed under the draft — by
   * a token-renew reload or by the row a 409 carried. Their draft entries were
   * re-seeded to the new value so they do not read as this operator's edits.
   */
  refreshedFields: string[];
  /** The last successful save this session, for the bar to acknowledge. */
  lastSaved: SavedNote | null;
  /** ISO time a draft written earlier was restored from, or null. */
  restoredAt: string | null;
  /** True when an earlier draft was dropped because the record had moved on. */
  droppedDraft: boolean;
  /** The "discard your changes?" prompt is open. */
  cancelPrompt: boolean;
  /** Why editing is unavailable, or null when it is available. */
  blocked: string | null;
  canEdit: boolean;
  /** Pending changes per group key, for badging a collapsed accordion. */
  pendingByGroup: Record<string, number>;
  /**
   * When the change took effect (SW-8879): ISO 8601 with an offset, or
   * null for now. The note travels with the change onto the timeline and
   * is required when a readiness-driving field is in the change-set.
   */
  effectiveAt: string | null;
  setEffectiveAt: (iso: string | null) => void;
  note: string;
  setNote: (note: string) => void;
  noteRequired: boolean;
  /**
   * When a later recorded change of a touched readiness field exists, its
   * time: a save dated before it rewrites the past and leaves today alone.
   */
  laterChangeAt: string | null;
  /** The tractor's zone, for the time picker. */
  tz: string;
  start: () => void;
  /** Start an edit session with one attestation already ticked. */
  startWithConfirm: (name: string) => void;
  /** Discard immediately, no questions — for callers that have already asked. */
  cancel: () => void;
  /** Discard, asking first when anything is pending. */
  requestCancel: () => void;
  dismissCancel: () => void;
  dismissNotes: () => void;
  updateField: (field: SchemaField, value: DraftValue) => void;
  /** Validate one field once the operator has left it. */
  blurField: (field: SchemaField) => void;
  toggleConfirm: (name: string, on: boolean) => void;
  confirmChecked: (name: string) => boolean;
  openReview: () => void;
  closeReview: () => void;
  submit: () => Promise<void>;
}

interface Options {
  vehicle: Vehicle;
  schema: ConfigSchema | null;
  /** Cognito ID token; absent in snapshot mode, which makes the panel read-only. */
  token?: string;
  /** Set when editing is unavailable, explaining why. */
  readOnlyReason?: string | null;
  /** Receives the server's authoritative row after any successful write. */
  onSaved: (row: VehicleRow) => void;
}

interface StoredDraft {
  configVersion: number | null;
  draft: Draft;
  confirm: string[];
  at: string;
}

const draftKey = (vehicleName: string) => `tractorState.draft.${vehicleName}`;

/**
 * The zone the time picker works in. The config record has no time-zone
 * field; the activity store keeps one per tractor with this default, and
 * every tractor runs on it today.
 */
const TRACTOR_TZ = "America/New_York";

function readStoredDraft(vehicleName: string): StoredDraft | null {
  try {
    const raw = window.sessionStorage.getItem(draftKey(vehicleName));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const stored = parsed as Partial<StoredDraft>;
    if (!stored.draft || typeof stored.draft !== "object") return null;
    return {
      configVersion:
        typeof stored.configVersion === "number" ? stored.configVersion : null,
      draft: stored.draft,
      confirm: Array.isArray(stored.confirm) ? stored.confirm : [],
      at: typeof stored.at === "string" ? stored.at : new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

function writeStoredDraft(vehicleName: string, value: StoredDraft) {
  try {
    window.sessionStorage.setItem(draftKey(vehicleName), JSON.stringify(value));
  } catch {
    // Quota or private mode: the draft simply does not survive the exit.
  }
}

function clearStoredDraft(vehicleName: string) {
  try {
    window.sessionStorage.removeItem(draftKey(vehicleName));
  } catch {
    // Nothing to clear.
  }
}

const sameDraft = (a: DraftValue | undefined, b: DraftValue | undefined) =>
  JSON.stringify(a ?? "") === JSON.stringify(b ?? "");

export function useConfigEditor({
  vehicle,
  schema,
  token,
  readOnlyReason,
  onSaved,
}: Options): ConfigEditor {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [reviewing, setReviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [conflictFields, setConflictFields] = useState<string[] | null>(null);
  const [refreshedFields, setRefreshedFields] = useState<string[]>([]);
  const [confirmSet, setConfirmSet] = useState<string[]>([]);
  const [lastSaved, setLastSaved] = useState<SavedNote | null>(null);
  const [restoredAt, setRestoredAt] = useState<string | null>(null);
  const [droppedDraft, setDroppedDraft] = useState(false);
  const [cancelPrompt, setCancelPrompt] = useState(false);
  const [effectiveAt, setEffectiveAt] = useState<string | null>(null);
  const [note, setNote] = useState("");

  const fields = useMemo(() => schema?.fields ?? [], [schema]);

  /**
   * The row the draft was seeded from. `buildChanges` diffs against the
   * *current* row, which is right for the save; this is what lets a refresh
   * of that row tell "the operator changed this" from "the server did".
   */
  const baseRef = useRef<Record<string, Vehicle["attrs"][string]>>({});
  /** Which tractor the draft in state belongs to. */
  const draftForRef = useRef<string | null>(null);

  const { changes, list } = useMemo(
    () => buildChanges(fields, vehicle, draft),
    [fields, vehicle, draft],
  );

  // A field being edited already restamps its own confirmation, and the API
  // collapses the duplicate — so drop it from the confirm list rather than
  // showing the operator two ways to say the same thing.
  const dirtyNames = useMemo(
    () => new Set(list.map((c) => c.field.name)),
    [list],
  );
  const confirmations = useMemo(
    () =>
      fields.filter(
        (f) => confirmSet.includes(f.name) && !dirtyNames.has(f.name),
      ),
    [fields, confirmSet, dirtyNames],
  );
  // The row as the draft leaves it, and what the rule table says about it:
  // which options are refused right now, and which stored values the save
  // will flip. Both read the schema's `rules`; a schema without them yields
  // nothing, so an older API gets the console it always had.
  const effective = useMemo(
    () => effectiveAttrs(vehicle, list),
    [vehicle, list],
  );
  const cascades = useMemo(
    () => ruleCascades(schema, vehicle, list),
    [schema, vehicle, list],
  );
  const blockedFor = useCallback(
    (field: SchemaField) => blockedOptions(schema, field, effective),
    [schema, effective],
  );
  const pendingCount = list.length + confirmations.length + cascades.length;
  // A readiness change draws on the timeline; the note is what makes the
  // segment say why. Cascades count: the rule moves the mark on the
  // operator's behalf, and the mark is what the lane shows.
  const noteRequired = useMemo(
    () =>
      touchesReadiness([
        ...list.map((c) => c.field.name),
        ...cascades.map((c) => c.field.name),
      ]),
    [list, cascades],
  );
  const laterChangeAt = useMemo(() => {
    if (!effectiveAt) return null;
    const touched = new Set(
      [...list, ...cascades]
        .map((c) => c.field.name)
        .filter((name) => READINESS_FIELDS.includes(name)),
    );
    const since = Date.parse(effectiveAt);
    let latest: string | null = null;
    for (const entry of vehicle.history) {
      const at = entry.effective_at ?? entry.at;
      if (!(Date.parse(at) > since)) continue;
      if (!Object.keys(entry.changes ?? {}).some((n) => touched.has(n)))
        continue;
      if (!latest || Date.parse(at) > Date.parse(latest)) latest = at;
    }
    return latest;
  }, [effectiveAt, list, cascades, vehicle.history]);
  // The operator's own change into a conflict (a Yes set before the move to
  // PMI). Live rather than stored with the blur-time errors: it is caused by
  // *another* field, so it has to clear the moment that field moves back,
  // which a message keyed to this field's own edits would never do.
  const ruleFound = useMemo(
    () => ruleErrors(schema, vehicle, list),
    [schema, vehicle, list],
  );
  const allErrors = useMemo(
    () => ({ ...errors, ...ruleFound }),
    [errors, ruleFound],
  );

  // Which groups hold pending work. With the groups collapsed by default, an
  // edit can be off-screen inside a closed drawer — the count on the header is
  // what keeps a save from including something the operator has forgotten is
  // there.
  const pendingByGroup = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const change of list) {
      counts[change.field.group] = (counts[change.field.group] ?? 0) + 1;
    }
    for (const field of confirmations) {
      counts[field.group] = (counts[field.group] ?? 0) + 1;
    }
    for (const change of cascades) {
      counts[change.field.group] = (counts[change.field.group] ?? 0) + 1;
    }
    return counts;
  }, [list, confirmations, cascades]);

  const editableCount = fields.filter(isEditable).length;
  const blocked = readOnlyReason ?? (!token ? "Sign in to edit." : null);
  const canEdit = !blocked && editableCount > 0;
  const hasErrors = Object.keys(allErrors).length > 0;

  const labelsFor = useCallback(
    (names: string[]) =>
      names.map((name) => fieldByName(schema, name)?.label ?? name),
    [schema],
  );

  const resetSession = useCallback(() => {
    setEditing(false);
    setDraft({});
    setErrors({});
    setSaveError(null);
    setConfirmSet([]);
    setConflictFields(null);
    setRefreshedFields([]);
    setCancelPrompt(false);
    setReviewing(false);
    setEffectiveAt(null);
    setNote("");
    draftForRef.current = null;
  }, []);

  const begin = useCallback(
    (seed: Draft, confirm: string[]) => {
      setDraft(seed);
      setErrors({});
      setSaveError(null);
      setConflictFields(null);
      setRefreshedFields([]);
      setConfirmSet(confirm);
      setLastSaved(null);
      setEditing(true);
      baseRef.current = { ...vehicle.attrs };
      draftForRef.current = vehicle.vehicleName;
    },
    [vehicle],
  );

  const start = useCallback(() => {
    setRestoredAt(null);
    setDroppedDraft(false);
    begin(draftFromVehicle(fields, vehicle), []);
  }, [begin, fields, vehicle]);

  const startWithConfirm = useCallback(
    (name: string) => {
      setRestoredAt(null);
      setDroppedDraft(false);
      begin(draftFromVehicle(fields, vehicle), [name]);
    },
    [begin, fields, vehicle],
  );

  const cancel = useCallback(() => {
    clearStoredDraft(vehicle.vehicleName);
    resetSession();
  }, [resetSession, vehicle.vehicleName]);

  const requestCancel = useCallback(() => {
    if (pendingCount > 0) setCancelPrompt(true);
    else cancel();
  }, [pendingCount, cancel]);

  const dismissCancel = useCallback(() => setCancelPrompt(false), []);

  const dismissNotes = useCallback(() => {
    setRestoredAt(null);
    setDroppedDraft(false);
    setLastSaved(null);
  }, []);

  /**
   * Validation runs when the operator *leaves* a field, not on every keystroke:
   * "Must be an https:// URL" appearing under a field that so far reads "h" is
   * noise. Once a field has an error, though, it re-validates live, so the
   * message clears the moment the value is acceptable rather than waiting for
   * another blur.
   */
  const updateField = useCallback((field: SchemaField, value: DraftValue) => {
    setDraft((prev) => ({ ...prev, [field.name]: value }));
    setErrors((prev) => {
      if (!(field.name in prev)) return prev;
      const next = { ...prev };
      const message = validateDraft(field, value);
      if (message) next[field.name] = message;
      else delete next[field.name];
      return next;
    });
  }, []);

  const blurField = useCallback(
    (field: SchemaField) => {
      const value = draft[field.name];
      if (value === undefined) return;
      setErrors((prev) => {
        const next = { ...prev };
        const message = validateDraft(field, value);
        if (message) next[field.name] = message;
        else delete next[field.name];
        return next;
      });
    },
    [draft],
  );

  const toggleConfirm = useCallback((name: string, on: boolean) => {
    setConfirmSet((prev) =>
      on ? [...prev, name] : prev.filter((n) => n !== name),
    );
  }, []);

  const confirmChecked = useCallback(
    (name: string) => confirmSet.includes(name),
    [confirmSet],
  );

  const openReview = useCallback(() => {
    // Type-level checks on the whole draft, then the live rule refusals: a Yes
    // set into PMI stops here, with the reason the API would give, instead of
    // as a 400 under the Save button.
    const found = validateAll(fields, draft);
    setErrors(found);
    if (
      Object.keys(found).length === 0 &&
      Object.keys(ruleFound).length === 0
    ) {
      setSaveError(null);
      setReviewing(true);
    }
  }, [fields, draft, ruleFound]);

  const closeReview = useCallback(() => setReviewing(false), []);

  const submit = useCallback(async () => {
    if (!token) return;
    setSaving(true);
    setSaveError(null);
    const count = pendingCount;
    if (noteRequired && !note.trim()) {
      setSaveError("A note is required when production readiness changes.");
      setSaving(false);
      return;
    }
    try {
      const updated = await patchVehicle(
        token,
        vehicle.vehicleName,
        changes,
        vehicle.configVersion,
        confirmations.map((f) => f.name),
        { effectiveAt, note },
      );
      clearStoredDraft(vehicle.vehicleName);
      onSaved(updated);
      resetSession();
      setLastSaved({
        count,
        version:
          typeof updated.config_version === "number"
            ? updated.config_version
            : null,
        at: Date.now(),
      });
    } catch (err) {
      if (err instanceof ConfigConflictError) {
        // Adopt the server's row as the new base and keep the draft, so the
        // operator sees their edits re-diffed against reality rather than
        // losing them to a "please try again". Name the fields that moved
        // underneath — those are the ones worth a second look before resaving.
        // Untouched fields are re-seeded by the effect below, once the new row
        // has landed, so a colleague's edit never shows up as this operator's.
        const fresh = vehicleFromRow(err.current);
        const moved = Object.keys(changes).filter(
          (name) =>
            JSON.stringify(fresh.attrs[name] ?? null) !==
            JSON.stringify(vehicle.attrs[name] ?? null),
        );
        onSaved(err.current);
        setConflictFields(labelsFor(moved));
        setReviewing(false);
        setSaveError(null);
      } else if (err instanceof FleetStateApiError) {
        if (err.status === 401) {
          setSaveError(
            "Your session has expired. Your changes are kept — sign in again to save them.",
          );
        } else if (err.fields.length) {
          const names = labelsFor(err.fields).join(", ");
          setSaveError(
            `${err.message} — nothing was saved, because an edit is applied in full or not at all. Fields you can’t change: ${names}.`,
          );
        } else if (err.status >= 500) {
          setSaveError(
            "The fleet API isn’t answering right now. Nothing was saved — try again in a moment.",
          );
        } else {
          setSaveError(err.message);
        }
      } else {
        setSaveError("Couldn’t reach the API. Nothing was saved.");
      }
    } finally {
      setSaving(false);
    }
  }, [
    token,
    vehicle,
    changes,
    confirmations,
    onSaved,
    pendingCount,
    resetSession,
    labelsFor,
    noteRequired,
    note,
    effectiveAt,
  ]);

  // -- The draft survives the component ------------------------------------

  // Persist on every change while editing, under the tractor the draft is for.
  useEffect(() => {
    if (!editing || !draftForRef.current) return;
    writeStoredDraft(draftForRef.current, {
      configVersion: vehicle.configVersion,
      draft,
      confirm: confirmSet,
      at: new Date().toISOString(),
    });
    // vehicle.configVersion is read at write time on purpose: it is the version
    // the draft is valid against, which changes only when the row is adopted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, draft, confirmSet]);

  // The tab is the one exit sessionStorage does not survive.
  useEffect(() => {
    if (!editing || pendingCount === 0) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Chrome still requires a returnValue to show the prompt.
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [editing, pendingCount]);

  // Switching tractors (the rail, browser history within the route) and
  // restoring a draft written earlier for this one.
  useEffect(() => {
    if (draftForRef.current && draftForRef.current !== vehicle.vehicleName) {
      // The view stayed mounted and the tractor under it changed. The draft
      // for the previous tractor is already in sessionStorage; carrying it
      // onto this one would edit the wrong record.
      resetSession();
    }
    if (!canEdit) return;
    const stored = readStoredDraft(vehicle.vehicleName);
    if (!stored) return;
    if (stored.configVersion !== vehicle.configVersion) {
      clearStoredDraft(vehicle.vehicleName);
      setDroppedDraft(true);
      return;
    }
    setDroppedDraft(false);
    setRestoredAt(stored.at);
    begin(stored.draft, stored.confirm);
    // Runs when the tractor changes, and once the page becomes editable (the
    // schema and token land after the first render). Not on every render of
    // `vehicle`: a refreshed row must not restart the session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle.vehicleName, canEdit]);

  // The row refreshed underneath an open edit — a token-renew reload, or the
  // row a 409 carried. Fields the operator has not touched follow the server;
  // fields they have keep their draft and are re-diffed against the new value.
  useEffect(() => {
    if (!editing || draftForRef.current !== vehicle.vehicleName) return;
    const previous = baseRef.current;
    const refreshed: string[] = [];
    const next: Draft = { ...draft };
    for (const field of fields) {
      if (!isEditable(field)) continue;
      const before = previous[field.name] ?? null;
      const after = vehicle.attrs[field.name] ?? null;
      if (JSON.stringify(before) === JSON.stringify(after)) continue;
      const untouched = sameDraft(draft[field.name], toDraft(field, before));
      if (untouched) {
        next[field.name] = toDraft(field, after);
        refreshed.push(field.label);
      }
    }
    baseRef.current = { ...vehicle.attrs };
    if (refreshed.length) {
      setDraft(next);
      setRefreshedFields((prev) => [...prev, ...refreshed]);
    }
    // Reacts to the row's identity changing, which is the only time the base
    // can have moved; `draft`/`fields` are read for the comparison, not
    // watched — a keystroke must not re-run the reconciliation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle]);

  return {
    editing,
    draft,
    errors: allErrors,
    confirmations,
    dirtyNames,
    list,
    cascades,
    blockedFor,
    pendingCount,
    hasErrors,
    saving,
    reviewing,
    saveError,
    conflictFields,
    refreshedFields,
    lastSaved,
    restoredAt,
    droppedDraft,
    cancelPrompt,
    blocked,
    canEdit,
    pendingByGroup,
    start,
    startWithConfirm,
    cancel,
    requestCancel,
    dismissCancel,
    dismissNotes,
    updateField,
    blurField,
    toggleConfirm,
    confirmChecked,
    openReview,
    closeReview,
    submit,
    effectiveAt,
    setEffectiveAt,
    note,
    setNote,
    noteRequired,
    laterChangeAt,
    tz: TRACTOR_TZ,
  };
}
